import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Globe, Code2, Layers, Zap, Brain, RefreshCw,
  ArrowRight, Star, CheckCircle, ChevronRight
} from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { t } from '../translations';
import { useScrollReveal } from '../hooks/useScrollReveal';

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/a2573c7b-5ba0-4f9f-8ac5-e06be66137b5/images/af32aa3f416200451e5a209afc35596d58ac401da28a454202e832be083b6db3.png";

// Image premium pour la section "Pourquoi nous"
const PREMIUM_IMG = "https://customer-assets.emergentagent.com/job_connected-work/artifacts/hpu1lk2c_ChatGPT%20Image%2014%20mai%202026%2C%2016_15_02.png";

// Images des vraies réalisations
const PROJECT_IMGS = [
  "https://customer-assets.emergentagent.com/job_connected-work/artifacts/z7xoesbs_E2DAE32C-FC28-480A-82F7-2C22D3FC8514.png", // Site Construction
  "https://customer-assets.emergentagent.com/job_connected-work/artifacts/k8aburmo_E088F2AA-6C4A-4E33-978A-8F23A3FB2723.png", // Site Plomberie
  "https://customer-assets.emergentagent.com/job_connected-work/artifacts/66utq2we_E1840B20-EFC3-4B4E-891E-21B6BEEA144B.png", // Site Restaurant
];

const iconMap = { Globe, Code2, Layers, Zap, Brain, RefreshCw };

function HeroSection({ tr }) {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ backgroundColor: '#0A1A1A' }}
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          alt=""
          className="w-full h-full object-cover opacity-20"
        />
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(to bottom, rgba(10,26,26,0.6) 0%, rgba(10,26,26,0.85) 60%, #0A1A1A 100%)' }}
        />
      </div>

      {/* Glow orbs */}
      <div
        className="absolute top-1/3 left-1/4 w-96 h-96 rounded-full opacity-10 blur-3xl pointer-events-none"
        style={{ background: 'radial-gradient(circle, #4F8EF7, transparent)' }}
      />
      <div
        className="absolute top-1/2 right-1/4 w-80 h-80 rounded-full opacity-10 blur-3xl pointer-events-none"
        style={{ background: 'radial-gradient(circle, #9B59F5, transparent)' }}
      />

      {/* Content */}
      <div className="relative section-container pt-24 pb-16 text-center">
        <div className="max-w-4xl mx-auto">
          {/* Agence tag — no border pill, just clean gradient text */}
          <div className="flex items-center justify-center gap-2 mb-7 animate-fadeInUp">
            <span className="w-1.5 h-1.5 rounded-full bg-exevori-primary animate-pulse" />
            <span className="gradient-text font-sora font-bold text-sm uppercase tracking-[0.18em]">{tr.hero.overline}</span>
            <span className="w-1.5 h-1.5 rounded-full bg-exevori-secondary animate-pulse" />
          </div>

          {/* H1 */}
          <h1 className="font-sora font-black text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mb-6 animate-fadeInUp delay-100">
            {tr.hero.title.split(',')[0]},
            <br />
            <span className="gradient-text">{tr.hero.title.split(',').slice(1).join(',')}</span>
          </h1>

          {/* Subtitle */}
          <p className="text-gray-300 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-10 animate-fadeInUp delay-200 font-inter">
            {tr.hero.subtitle}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fadeInUp delay-300">
            <Link to="/devis" data-testid="hero-cta-quote" className="btn-primary text-base px-8 py-4">
              {tr.hero.cta1} <ArrowRight size={18} />
            </Link>
            <Link to="/services" data-testid="hero-cta-services" className="btn-secondary text-base px-8 py-4">
              {tr.hero.cta2}
            </Link>
          </div>

          {/* Stats row */}
          <div className="mt-16 grid grid-cols-3 gap-6 max-w-lg mx-auto animate-fadeInUp delay-400">
            {[
              { val: '✓', label: 'Qualité garantie' },
              { val: '✓', label: 'Satisfaction client' },
              { val: '✓', label: 'Accompagnement personnalisé' },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className="font-sora font-black text-2xl gradient-text">{s.val}</div>
                <div className="text-gray-400 text-xs mt-1 font-inter">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce opacity-50">
        <div className="w-6 h-10 border-2 border-white/20 rounded-full flex justify-center pt-2">
          <div className="w-1 h-2 bg-exevori-primary rounded-full" />
        </div>
      </div>
    </section>
  );
}

function CredibilitySection({ tr }) {
  const [ref, visible] = useScrollReveal();
  return (
    <section ref={ref} className="py-16" style={{ backgroundColor: '#0D1F1F' }}>
      <div className="section-container">
        <div className={`text-center mb-10 reveal ${visible ? 'visible' : ''}`}>
          <h2 className="font-sora font-bold text-xl md:text-2xl text-white">{tr.credibility.title}</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {tr.credibility.items.map((item, i) => (
            <div
              key={i}
              data-testid={`credibility-item-${i}`}
              className={`exevori-card p-5 text-center reveal ${visible ? 'visible' : ''}`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div
                className="w-10 h-10 rounded-xl mx-auto mb-3 flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, rgba(79,142,247,0.2), rgba(155,89,245,0.2))' }}
              >
                <div className="w-3 h-3 rounded-full gradient-bg" />
              </div>
              <div className="font-sora font-semibold text-white text-sm mb-1">{item.title}</div>
              <div className="text-gray-400 text-xs font-inter">{item.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServicesPreview({ tr }) {
  const [ref, visible] = useScrollReveal();
  return (
    <section ref={ref} className="py-24" style={{ backgroundColor: '#0A1A1A' }}>
      <div className="section-container">
        <div className={`text-center mb-16 reveal ${visible ? 'visible' : ''}`}>
          <h2 className="font-sora font-bold text-3xl md:text-4xl text-white mb-3">{tr.services.title}</h2>
          <div className="w-12 h-0.5 gradient-bg rounded-full mx-auto mb-5" />
          <p className="text-gray-300 text-base max-w-2xl mx-auto font-inter">{tr.services.subtitle}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tr.services.items.map((svc, i) => {
            const Icon = iconMap[svc.icon] || Globe;
            return (
              <div
                key={i}
                data-testid={`service-card-${i}`}
                className={`exevori-card p-8 group reveal ${visible ? 'visible' : ''}`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div
                  className="w-12 h-12 rounded-xl mb-5 flex items-center justify-center group-hover:scale-110 transition-transform"
                  style={{ background: 'linear-gradient(135deg, rgba(79,142,247,0.15), rgba(155,89,245,0.15))' }}
                >
                  <Icon size={22} className="text-exevori-primary" strokeWidth={1.5} />
                </div>
                <h3 className="font-sora font-semibold text-white text-lg mb-3">{svc.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed font-inter mb-5">{svc.desc}</p>
                <Link
                  to="/services"
                  className="text-exevori-primary text-sm font-medium font-inter flex items-center gap-1 hover:gap-2 transition-all"
                >
                  {tr.services.learnMore} <ChevronRight size={14} />
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function WhyExevori({ tr }) {
  const [ref, visible] = useScrollReveal();
  return (
    <section ref={ref} className="py-24" style={{ backgroundColor: '#0D1F1F' }}>
      <div className="section-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className={`reveal-left ${visible ? 'visible' : ''}`}>
            <h2 className="font-sora font-bold text-3xl md:text-4xl text-white mb-2">{tr.whyExevori.title}</h2>
            <div className="w-10 h-0.5 gradient-bg rounded-full mb-6" />
            <div className="space-y-6">
              {tr.whyExevori.items.map((item, i) => (
                <div
                  key={i}
                  data-testid={`why-item-${i}`}
                  className="flex items-start gap-4"
                >
                  <div
                    className="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center mt-0.5"
                    style={{ background: 'linear-gradient(135deg, rgba(79,142,247,0.2), rgba(155,89,245,0.2))' }}
                  >
                    <CheckCircle size={16} className="text-exevori-primary" />
                  </div>
                  <div>
                    <h4 className="font-sora font-semibold text-white mb-1">{item.title}</h4>
                    <p className="text-gray-400 text-sm font-inter leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/devis" data-testid="why-cta" className="btn-primary mt-8 inline-flex">
              {tr.hero.cta1} <ArrowRight size={16} />
            </Link>
          </div>
          <div className={`reveal-right ${visible ? 'visible' : ''}`}>
            <div className="relative">
              <div
                className="absolute inset-0 rounded-3xl blur-2xl opacity-20"
                style={{ background: 'linear-gradient(135deg, #4F8EF7, #9B59F5)' }}
              />
              <img
                src={PREMIUM_IMG}
                alt="Exevori solutions"
                className="w-full h-auto rounded-3xl relative z-10 shadow-2xl"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PortfolioPreview({ tr }) {
  const [ref, visible] = useScrollReveal();
  return (
    <section ref={ref} className="py-24" style={{ backgroundColor: '#0A1A1A' }}>
      <div className="section-container">
        <div className={`flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-16 reveal ${visible ? 'visible' : ''}`}>
          <div>
            <h2 className="font-sora font-bold text-3xl md:text-4xl text-white">{tr.portfolio.title}</h2>
            <div className="w-10 h-0.5 gradient-bg rounded-full mt-3" />
          </div>
          <Link to="/realisations" data-testid="portfolio-view-all" className="btn-secondary whitespace-nowrap">
            {tr.portfolio.viewAll} <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {tr.portfolio.projects.map((proj, i) => (
            <div
              key={i}
              data-testid={`portfolio-preview-${i}`}
              className={`exevori-card overflow-hidden group reveal ${visible ? 'visible' : ''}`}
              style={{ transitionDelay: `${i * 120}ms` }}
            >
              {/* Cadre navigateur pour les vraies captures */}
              <div className="relative overflow-hidden bg-gray-900">
                <div className="flex items-center gap-1.5 px-3 py-2 bg-gray-800 border-b border-white/5">
                  <div className="w-2 h-2 rounded-full bg-red-500/70"></div>
                  <div className="w-2 h-2 rounded-full bg-yellow-500/70"></div>
                  <div className="w-2 h-2 rounded-full bg-green-500/70"></div>
                </div>
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={PROJECT_IMGS[i]}
                    alt={proj.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-exevori-darker/80 to-transparent" />
                </div>
                <span
                  className="absolute top-10 right-3 px-2 py-1 rounded-lg text-xs font-medium font-inter"
                  style={{ background: 'rgba(79,142,247,0.2)', color: '#4F8EF7', border: '1px solid rgba(79,142,247,0.3)' }}
                >
                  {proj.type}
                </span>
              </div>
              <div className="p-6">
                <div className="text-gray-400 text-xs mb-2 font-inter">{proj.client}</div>
                <h3 className="font-sora font-semibold text-white text-lg mb-2">{proj.name}</h3>
                <p className="text-gray-400 text-sm font-inter leading-relaxed mb-4 line-clamp-2">{proj.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {proj.tags.slice(0, 3).map((tag, j) => (
                    <span key={j} className="px-2 py-1 text-xs rounded-lg font-inter text-gray-400 border border-white/10">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProcessPreview({ tr }) {
  const [ref, visible] = useScrollReveal();
  return (
    <section ref={ref} className="py-24" style={{ backgroundColor: '#0D1F1F' }}>
      <div className="section-container">
        <div className={`text-center mb-16 reveal ${visible ? 'visible' : ''}`}>
          <h2 className="font-sora font-bold text-3xl md:text-4xl text-white mb-3">{tr.process.title}</h2>
          <div className="w-12 h-0.5 gradient-bg rounded-full mx-auto mb-5" />
          <p className="text-gray-300 text-base max-w-2xl mx-auto font-inter">{tr.process.subtitle}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
          {tr.process.steps.map((step, i) => (
            <div
              key={i}
              data-testid={`process-step-${i}`}
              className={`text-center reveal ${visible ? 'visible' : ''}`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div
                className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center font-sora font-black text-lg"
                style={{ background: 'linear-gradient(135deg, #4F8EF7, #9B59F5)' }}
              >
                {step.num}
              </div>
              <h4 className="font-sora font-semibold text-white mb-2">{step.title}</h4>
              <p className="text-gray-400 text-sm font-inter leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>
        <div className={`text-center mt-12 reveal ${visible ? 'visible' : ''}`}>
          <Link to="/processus" data-testid="process-learn-more" className="btn-secondary">
            {tr.services.learnMore} <ChevronRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function Testimonials({ tr }) {
  const [ref, visible] = useScrollReveal();
  
  // Ne pas afficher la section si pas de témoignages
  if (!tr.testimonials.items || tr.testimonials.items.length === 0) {
    return null;
  }
  
  return (
    <section ref={ref} className="py-24" style={{ backgroundColor: '#0A1A1A' }}>
      <div className="section-container">
        <div className={`text-center mb-16 reveal ${visible ? 'visible' : ''}`}>
          <h2 className="font-sora font-bold text-3xl md:text-4xl text-white mb-3">{tr.testimonials.title}</h2>
          <div className="w-12 h-0.5 gradient-bg rounded-full mx-auto" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {tr.testimonials.items.map((t, i) => (
            <div
              key={i}
              data-testid={`testimonial-${i}`}
              className={`exevori-card p-8 reveal ${visible ? 'visible' : ''}`}
              style={{ transitionDelay: `${i * 120}ms` }}
            >
              <div className="flex gap-1 mb-4">
                {Array.from({ length: t.rating }).map((_, j) => (
                  <Star key={j} size={16} className="text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <p className="text-gray-300 text-sm font-inter leading-relaxed mb-6 italic">"{t.text}"</p>
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center font-sora font-bold text-sm"
                  style={{ background: 'linear-gradient(135deg, #4F8EF7, #9B59F5)' }}
                >
                  {t.name.charAt(0)}
                </div>
                <div>
                  <div className="font-sora font-semibold text-white text-sm">{t.name}</div>
                  <div className="text-gray-400 text-xs font-inter">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA({ tr }) {
  const [ref, visible] = useScrollReveal();
  return (
    <section
      ref={ref}
      className="py-32 relative overflow-hidden"
      style={{ backgroundColor: '#0D1F1F' }}
    >
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: 'radial-gradient(circle at 30% 50%, rgba(79,142,247,0.3) 0%, transparent 50%), radial-gradient(circle at 70% 50%, rgba(155,89,245,0.3) 0%, transparent 50%)',
        }}
      />
      <div className="section-container relative">
        <div className={`text-center max-w-2xl mx-auto reveal ${visible ? 'visible' : ''}`}>
          <h2 className="font-sora font-black text-3xl md:text-5xl text-white mb-6">
            {tr.finalCta.title}
          </h2>
          <p className="text-gray-300 text-base md:text-lg font-inter mb-10">{tr.finalCta.subtitle}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/devis" data-testid="final-cta-quote" className="btn-primary text-base px-8 py-4">
              {tr.finalCta.cta1} <ArrowRight size={18} />
            </Link>
            <Link to="/contact" data-testid="final-cta-contact" className="btn-secondary text-base px-8 py-4">
              {tr.finalCta.cta2}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  const { lang } = useLang();
  const tr = t[lang];

  return (
    <main data-testid="home-page">
      <HeroSection tr={tr} />
      <CredibilitySection tr={tr} />
      <ServicesPreview tr={tr} />
      <WhyExevori tr={tr} />
      <PortfolioPreview tr={tr} />
      <ProcessPreview tr={tr} />
      <Testimonials tr={tr} />
      <FinalCTA tr={tr} />
    </main>
  );
}
